import type { InputButton, PlayerInput, SeatId } from "@arcade-hockey/shared";
import { type GamepadSnapshot, type KeyboardTracker, applyDeadzoneTo } from "@neoweb/input";
import {
  type GamepadBinding,
  type KeyBinding,
  STICK_DEADZONE,
  defaultGamepadBinding,
  defaultKeyBinding,
} from "./bindings.js";

const TRIGGER_PRESSED_THRESHOLD = 0.5;

export type SeatSource =
  | { kind: "Keyboard"; binding: KeyBinding }
  | { kind: "Gamepad"; index: number; binding: GamepadBinding };

const MOVE_SCRATCH = { x: 0, y: 0 };

/**
 * One mapper per seat. Owns the per-seat seq counter and a precomputed
 * iteration list over its button binding (avoids per-tick Object.entries).
 * buildInputInto writes into a caller-owned PlayerInput; allocation-free.
 */
export class SeatInputMapper {
  private seq = 0;
  private readonly buttonFlags: number[] = [];
  private readonly buttonKeys: string[] = []; // keyboard codes
  private readonly buttonIndices: number[] = []; // gamepad button indices

  constructor(
    readonly seatId: SeatId,
    readonly source: SeatSource,
  ) {
    if (source.kind === "Keyboard") {
      for (const [flagStr, code] of Object.entries(source.binding.buttons)) {
        if (code === undefined) continue;
        this.buttonFlags.push(Number(flagStr));
        this.buttonKeys.push(code);
      }
    } else {
      for (const [flagStr, idx] of Object.entries(source.binding.buttons)) {
        if (idx === undefined) continue;
        this.buttonFlags.push(Number(flagStr));
        this.buttonIndices.push(idx);
      }
    }
  }

  buildInputInto(
    out: PlayerInput,
    args: {
      tick: number;
      keyboard: KeyboardTracker;
      gamepads: GamepadSnapshot[];
    },
  ): void {
    const { tick, keyboard, gamepads } = args;
    const source = this.source;
    let moveX = 0;
    let moveY = 0;
    let sprint = 0;
    let buttons = 0;

    if (source.kind === "Keyboard") {
      const b = source.binding;
      const mx = (keyboard.isHeld(b.move.right) ? 1 : 0) - (keyboard.isHeld(b.move.left) ? 1 : 0);
      const my = (keyboard.isHeld(b.move.down) ? 1 : 0) - (keyboard.isHeld(b.move.up) ? 1 : 0);
      const moveMag = Math.hypot(mx, my);
      if (moveMag > 0) {
        moveX = mx / moveMag;
        moveY = my / moveMag;
      }
      sprint = keyboard.isHeld(b.sprint) ? 1 : 0;
      const flags = this.buttonFlags;
      const codes = this.buttonKeys;
      for (let i = 0; i < flags.length; i++) {
        if (keyboard.isHeld(codes[i] as string)) {
          buttons |= flags[i] as InputButton;
        }
      }
    } else {
      let pad: GamepadSnapshot | undefined;
      for (const g of gamepads) {
        if (g.index === source.index) {
          pad = g;
          break;
        }
      }
      if (pad !== undefined) {
        const b = source.binding;
        applyDeadzoneTo(
          MOVE_SCRATCH,
          pad.axes[b.axes.moveX] ?? 0,
          pad.axes[b.axes.moveY] ?? 0,
          STICK_DEADZONE,
        );
        moveX = MOVE_SCRATCH.x;
        moveY = MOVE_SCRATCH.y;
        sprint = pad.triggers[b.sprintTriggerIndex] ?? 0;
        const flags = this.buttonFlags;
        const indices = this.buttonIndices;
        for (let i = 0; i < flags.length; i++) {
          const idx = indices[i] as number;
          const trigger = pad.triggers[idx] ?? 0;
          const pressed = pad.buttons[idx] ?? false;
          if (pressed || trigger > TRIGGER_PRESSED_THRESHOLD) {
            buttons |= flags[i] as InputButton;
          }
        }
      }
    }

    this.seq += 1;
    out.tick = tick;
    out.seq = this.seq;
    out.seatId = this.seatId;
    out.playerId = null;
    out.moveX = moveX;
    out.moveY = moveY;
    out.sprint = sprint;
    out.buttons = buttons;
  }
}

export function defaultP1KeyboardSource(): SeatSource {
  return { kind: "Keyboard", binding: defaultKeyBinding };
}

export function defaultGamepadSource(index: number): SeatSource {
  return { kind: "Gamepad", index, binding: defaultGamepadBinding };
}
